import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Download, Mail, Network, Plus, UserPlus, UsersRound } from 'lucide-react'
import { useState } from 'react'
import { Link, Navigate, useNavigate, useParams, useSearchParams } from 'react-router'
import { CircleColorPicker } from '@/components/CircleColor'
import { CircleName } from '@/components/CircleName'
import { CircleSpaces, SpaceDialog } from '@/components/CircleSpaces'
import { ConfirmButton } from '@/components/ConfirmButton'
import { CreateAction } from '@/components/CreateAction'
import { type Filter } from '@/components/Filters'
import { FormError } from '@/components/FormError'
import { GrowCircle } from '@/components/GrowCircle'
import { CircleAlbum, CircleAlbums } from '@/components/people/CircleAlbums'
import { useAddConnection } from '@/components/people/AddConnection'
import { Connect } from '@/components/people/Connect'
import { PeopleTabs, type PeopleTab } from '@/components/people/PeopleTabs'
import { useTabSwipe } from '@/components/modern/useTabSwipe'
import { SettingsCog } from '@/components/people/SettingsCog'
import { FromLinks } from '@/components/people/FromLinks'
import { InviteForm } from '@/components/people/InviteForm'
import { PersonProfile } from '@/components/people/PersonProfile'
import { useOpenPerson } from '@/lib/connections'
import { WaitingToJoin } from '@/components/people/WaitingToJoin'
import { page } from '@/components/layout'
import { PageLoader } from '@/components/Loader'
import { AddPerson } from '@/components/tree/AddPerson'
import { FamilyTree } from '@/components/tree/FamilyTree'
import { PeopleGroups } from '@/components/tree/PeopleViews'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { circleSections } from '@/lib/sections'
import { api, type Me } from '@/lib/api'
import { useCircles, useFamilies, type Family } from '@/lib/families'
import { forgetFamily, lastFamily, usePageFrame } from '@/lib/frame'
import { useFramily } from '@/lib/framily'
import { useMyPeople } from '@/lib/myPeople'
import { familyGroups, PEOPLE_VIEWS, type PeopleView } from '@/lib/peopleGroups'
import {
  circleCounts,
  fromMyPeople,
  fromTree,
  groupingFor,
  narrow,
  relationTo,
  PEOPLE_BY,
  PEOPLE_SCOPES,
  type PeopleBy,
  type PeopleScope,
  type PeopleWho,
  type ShownPerson,
} from '@/lib/peopleList'
import { remember, remembered } from '@/lib/remember'
import { useTree } from '@/lib/tree'
import { cn } from '@/lib/utils'
import { PlaceHeader, WithFamily } from './FamilyPage'

export const PeoplePage = ({ me }: { me: Me }) => (
  <WithFamily>
    {(family) =>
      // Everyone, and your own space unless you've given it a family tree: everyone you know, and connecting one to one.
      family.everyone || (family.personal && !circleSections(me.sections, family).people) ? (
        <YourPeople family={family} />
      ) : (
        <People family={family} me={me} />
      )
    }
  </WithFamily>
)

// People in Everyone or your own space. People: who wants to connect, people you may know, and everyone you know,
// from all your circles and your own connections. Circles: your circles like albums of faces. Any face opens their
// profile. The "+" connects with someone new (QR code, email, Search) or makes a circle.
function YourPeople({ family }: { family: Family }) {
  usePageFrame(family.id, { title: 'People' })
  const navigate = useNavigate()
  const people = useMyPeople()
  const circles = useCircles()
  const add = useAddConnection()
  const [params, setParams] = useSearchParams()
  const [tab, setTab] = useState<PeopleTab>(() =>
    params.has('circle') || firstView() === 'circles' ? 'circles' : 'people',
  )
  const [by, setBy] = useState<PeopleBy>(() => remembered(BY_KEY, PEOPLE_BY, 'name'))
  const [who, setWho] = useState<PeopleWho>('everyone')
  const [search, setSearch] = useState('')
  const [profile, setProfile] = useState<ShownPerson | null>(null)
  const openPerson = useOpenPerson(setProfile)
  const others = (people.data ?? []).filter((p) => !p.me)
  const several = others.some((p) => p.circles.length > 1)
  const grouping = groupingFor('framily', by)
  const shown = narrow(fromMyPeople(others, grouping), who, search)
  function changeTab(next: PeopleTab) {
    setTab(next)
    remember(VIEW_KEY, next === 'circles' ? 'circles' : 'connections')
    if (params.has('circle')) setParams({}, { replace: true })
  }
  const { root, tabRow, pager } = useTabSwipe({ keys: TABS, active: tab, still: !profile, onTurn: changeTab })
  const filters: Filter[] = [groupBy(grouping, 'circle', setBy), whoFilter(who, setWho)]
  const albumId = tab === 'circles' ? params.get('circle') : null
  const album = albumId ? circles.data?.find((c) => c.id === albumId) : undefined

  return (
    <div ref={root} className="flex flex-col gap-6 pt-6 nav:gap-8 nav:pt-14">
      <div className={cn(page, 'max-nav:sr-only')}>
        <PlaceHeader family={family} title="People" />
      </div>
      <CreateAction
        title="Add to people"
        groups={[add.actions, [{ label: 'Create a circle', icon: Plus, onSelect: () => navigate('/circle/new') }]]}
      />
      <PeopleTabs ref={tabRow} tab={tab} onTab={changeTab} search={search} onSearch={setSearch} filters={filters} />
      <div ref={pager} className={cn(page, 'flex min-h-[60svh] flex-col gap-8')}>
        {tab === 'people' ? (
          <>
            {family.personal && !search && (
              <div className="flex max-w-(--measure) flex-col items-start gap-4">
                <p className="text-muted-foreground">
                  This is your own space. What you keep here is only yours, until you share it. Connect with people you
                  know to share with Everyone, or create a circle for family or friends.
                </p>
                <Button asChild variant="outline">
                  <Link to="/circle/new">
                    <Plus aria-hidden /> Create a circle
                  </Link>
                </Button>
              </div>
            )}
            {!search && (
              <div className="max-w-(--measure)">
                <Connect />
              </div>
            )}
            <FormError error={people.error} />
            {shown.length > 0 ? (
              <section aria-labelledby="everyone-you-know" className="flex max-w-(--measure) flex-col gap-1">
                <h2 id="everyone-you-know" className="text-sm font-semibold">
                  {family.personal ? 'Your connections' : 'Everyone you know'}
                </h2>
                <PeopleGroups groups={shown} circles={several} onOpen={openPerson} />
              </section>
            ) : (
              (search || who !== 'everyone') && (
                <p className="text-muted-foreground">
                  No one here matches. Try another name, or Everyone under Filter.
                </p>
              )
            )}
          </>
        ) : (
          circles.data &&
          people.data &&
          (album ? (
            <CircleAlbum circle={album} people={people.data} onBack={() => setParams({})} onOpenPerson={openPerson} />
          ) : circles.data.length > 0 ? (
            <CircleAlbums
              circles={circles.data}
              people={people.data}
              here={family.id}
              onOpen={(id) => {
                setParams({ circle: id })
                window.scrollTo(0, 0)
              }}
            />
          ) : (
            <div className="flex max-w-(--measure) flex-col items-start gap-4">
              <p className="text-muted-foreground">
                You’re not in a circle yet. A circle is for family or friends: a family tree, and what you keep
                together.
              </p>
              <Button asChild variant="outline">
                <Link to="/circle/new">
                  <Plus aria-hidden /> Create a circle
                </Link>
              </Button>
            </div>
          ))
        )}
      </div>
      <PersonProfile person={profile} onClose={() => setProfile(null)} />
      {add.dialogs}
    </div>
  )
}

const TABS: PeopleTab[] = ['people', 'circles']

// Filter's Group by: no groups, or by circle (or, in one circle, by family on its tree).
const groupBy = (value: PeopleBy, other: 'circle' | 'family', set: (by: PeopleBy) => void): Filter => ({
  heading: 'Group by',
  label: 'Group people by',
  value,
  usual: 'name',
  options: [['name', 'None'], other === 'circle' ? ['circle', 'Circle'] : ['family', 'Family']],
  onChange: (v) => {
    set(v as PeopleBy)
    remember(BY_KEY, v)
  },
})

const whoFilter = (value: PeopleWho, set: (who: PeopleWho) => void): Filter => ({
  heading: 'Who',
  label: 'Show who',
  value,
  usual: 'everyone',
  options: WHO_LABEL,
  onChange: (v) => set(v as PeopleWho),
})

// /people (the link in a connection email): People in the circle you were last in.
export function PeopleNow() {
  const families = useFamilies()
  const list = families.data?.families ?? []
  const id = list.find((f) => f.id === lastFamily())?.id ?? list[0]?.id
  if (families.isPending) return <PageLoader />
  return <Navigate to={id ? `/circle/${id}/people` : '/'} replace />
}

export const CircleSettingsPage = ({ me }: { me: Me }) => (
  <WithFamily>{(family) => <CircleSettings family={family} me={me} />}</WithFamily>
)
export const KeepACopyPage = () => <WithFamily>{(family) => <KeepACopy family={family} />}</WithFamily>

const VIEW_KEY = 'gatheredfrom:people-view'
const SCOPE_KEY = 'gatheredfrom:people-scope'
// No grouping is the usual now; an earlier pick of Family or Circle isn't carried over.
const BY_KEY = 'gatheredfrom:people-group'
const WHO_LABEL: [PeopleWho, string][] = [
  ['everyone', 'Everyone'],
  ['joined', 'In the app'],
  ['waiting', 'Not joined yet'],
  ['memory', 'In memory'],
]

// Before there were Connections and Circles, People remembered cards or a list as its view.
const firstView = (): PeopleView => {
  const old = remembered(VIEW_KEY, [...PEOPLE_VIEWS, 'cards', 'list'], 'connections')
  return old === 'cards' || old === 'list' ? 'connections' : old
}

// Everyone you're connected to, two ways. Connections, the usual one: the people in all your circles as one compact
// list, narrowed to this circle or grouped under Filter (by circle, or in families read from this circle's tree). Circles: your circles like albums of
// faces, each opening on everyone in it with the way into the circle. Any face opens their profile, and from there
// their page. A circle with a family tree also has the tree, a button beside the switch. Invites still waiting are
// under Connections. Making things (a person, an invite, a space, a circle) is the "+" at the top right; renaming,
// spaces, keeping a copy and leaving are on the circle's settings page, the cog on its Home.
function People({ family, me }: { family: Family; me: Me }) {
  // With the People section switched off this is just the circle's own page: who's here, invites, and this circle.
  // A circle without a family tree (Grow this circle) has the same page, under People.
  const withTree = circleSections(me.sections, family).people
  const title = me.sections.people ? 'People' : 'Your circle'
  usePageFrame(family.id, { title })
  const { personId } = useParams()
  const [params, setParams] = useSearchParams()
  const navigate = useNavigate()
  const tree = useTree(family.id)
  const circles = useCircles()
  const framily = useFramily()
  const everyone = useMyPeople()
  const [chosen, setChosen] = useState<PeopleView>(firstView)
  const [scope, setScope] = useState<PeopleScope>(() => remembered(SCOPE_KEY, PEOPLE_SCOPES, 'framily'))
  const [by, setBy] = useState<PeopleBy>(() => remembered(BY_KEY, PEOPLE_BY, 'name'))
  const [who, setWho] = useState<PeopleWho>('everyone')
  const [search, setSearch] = useState('')
  const [profile, setProfile] = useState<ShownPerson | null>(null)
  const openPerson = useOpenPerson(setProfile)
  const someone = (tree.data?.people.length ?? 0) > 0
  // A link to someone in particular always opens the tree on them, and the tree only exists where the circle has one.
  const view: PeopleView = personId && withTree ? 'tree' : chosen === 'tree' && !withTree ? 'connections' : chosen
  function changeView(next: PeopleView) {
    setChosen(next)
    remember(VIEW_KEY, next)
    if (params.has('circle')) setParams({}, { replace: true })
    if (personId && next !== 'tree') navigate(`/circle/${family.id}/people`)
  }
  // Everyone is the same as this circle until you're in another, or connected to someone outside it.
  const direct = everyone.data?.some((p) => p.connected && p.circles.length === 0) ?? false
  const several = (circles.data?.length ?? 0) > 1 || direct
  const showing: PeopleScope = several ? scope : 'circle'
  const grouping = groupingFor(showing, by)

  // The "+" adds someone beside whoever the tree is on, else beside you.
  const [adding, setAdding] = useState(false)
  const [inviting, setInviting] = useState(false)
  const [makingSpace, setMakingSpace] = useState(false)
  const from = (view === 'tree' && personId) || tree.data?.me || tree.data?.people[0]?.id || ''
  const add = useAddConnection()
  const tab: PeopleTab = view === 'circles' ? 'circles' : 'people'
  const { root, tabRow, pager } = useTabSwipe({
    keys: TABS,
    active: tab,
    // The tree pans with a finger, so it's never swiped away.
    still: !profile && view !== 'tree',
    onTurn: (next) => changeView(next === 'circles' ? 'circles' : 'connections'),
  })

  const actions = [
    ...(withTree && someone ? [{ label: 'Add a person', icon: UserPlus, onSelect: () => setAdding(true) }] : []),
    // Nobody else is ever in your own space.
    ...(me.canInvite && !family.personal
      ? [{ label: 'Invite someone', icon: Mail, onSelect: () => setInviting(true) }]
      : []),
    ...(family.personal ? [] : [{ label: 'Make a space', icon: UsersRound, onSelect: () => setMakingSpace(true) }]),
    { label: 'Create a circle', icon: Plus, onSelect: () => navigate('/circle/new') },
  ]

  // People who joined but haven't said who they are on the tree; with no tree yet, that's everyone here.
  const offTree: ShownPerson[] = family.members
    .filter((x) => !withTree || !x.personId)
    .map((x) => ({
      id: x.id,
      name: x.name,
      portrait: x.portrait,
      pet: false,
      living: true,
      born: null,
      died: null,
      line: x.id === me.id ? 'You' : (family.households.find((h) => h.id === x.householdId)?.name ?? ''),
      circles: 0,
      joined: true,
      href: null,
      userId: x.id,
    }))
  const whosHere = { key: 'here', name: 'Who’s here', people: offTree }
  // All your circles: everyone you're connected to. This circle: its tree, in families, or who's here without one.
  // What someone is to you, when this circle's tree has them ("Your brother").
  const toYou = (id: string) => {
    const p = tree.data?.people.find((x) => x.id === id)
    return p && tree.data ? relationTo(tree.data)(p) : ''
  }
  const groups =
    showing === 'framily'
      ? everyone.data
        ? fromMyPeople(everyone.data, grouping, undefined, toYou)
        : []
      : withTree && tree.data && someone
        ? fromTree(
            tree.data,
            familyGroups(tree.data),
            grouping,
            relationTo(tree.data),
            circleCounts(framily.data ?? []),
            offTree,
            new Map(family.members.flatMap((x) => (x.personId ? [[x.personId, x.id] as [string, string]] : []))),
          )
        : [whosHere]
  const shown = narrow(groups, who, search)
  // A circle with a tree that no one is on yet.
  const startTree = withTree && !!tree.data && !someone

  const filters: Filter[] = [
    ...(several
      ? [
          {
            heading: 'Show',
            label: 'Show people from',
            value: showing,
            usual: 'framily',
            options: [
              ['framily', direct ? 'All my circles and connections' : 'All my circles'],
              ['circle', family.name],
            ] as [string, string][],
            onChange: (v: string) => {
              setScope(v as PeopleScope)
              remember(SCOPE_KEY, v)
            },
          },
        ]
      : []),
    groupBy(grouping, showing === 'framily' ? 'circle' : 'family', setBy),
    whoFilter(who, setWho),
  ]

  const albumId = view === 'circles' ? params.get('circle') : null
  const album = albumId ? circles.data?.find((c) => c.id === albumId) : undefined
  const openAlbum = (id: string) => {
    setParams({ circle: id })
    window.scrollTo(0, 0)
  }

  return (
    <div ref={root} className="flex flex-col gap-6 pt-6 nav:gap-8 nav:pt-14">
      <div className={cn(page, 'max-nav:sr-only')}>
        <PlaceHeader family={family} title={title} />
      </div>
      <CreateAction title="Add to people" groups={[add.actions, actions]} />
      {/* The tabs, search and Filter, and the family tree. */}
      <PeopleTabs
        ref={tabRow}
        tab={tab}
        onTab={(next) => changeView(next === 'circles' ? 'circles' : 'connections')}
        search={search}
        onSearch={setSearch}
        filters={filters}
        find={view === 'connections'}
        end={
          <>
            {withTree && (
              <Button
                variant={view === 'tree' ? 'default' : 'ghost'}
                size="icon"
                className="shrink-0 rounded-full pointer-coarse:size-11"
                aria-label="Family tree"
                aria-pressed={view === 'tree'}
                onClick={() => changeView(view === 'tree' ? 'connections' : 'tree')}
              >
                <Network aria-hidden />
              </Button>
            )}
            {!me.sections.home && <SettingsCog familyId={family.id} />}
          </>
        }
      />
      <div ref={pager} className={cn(page, 'flex min-h-[60svh] flex-col gap-8 nav:gap-10')}>
        {view === 'circles' &&
          circles.data &&
          everyone.data &&
          (album ? (
            <CircleAlbum circle={album} people={everyone.data} onBack={() => setParams({})} onOpenPerson={openPerson} />
          ) : (
            <CircleAlbums circles={circles.data} people={everyone.data} here={family.id} onOpen={openAlbum} />
          ))}

        {view === 'connections' && (
          <>
            <FromLinks family={family} />
            <WaitingToJoin family={family} canInvite={me.canInvite && !family.personal} />
            {!search && <Connect />}
          </>
        )}

        {/* A tree that hasn't started yet is started here, and then opens on whoever it began with. */}
        {(view === 'tree' || (view === 'connections' && startTree)) && (
          <div className="max-w-(--measure)">
            <FamilyTree
              familyId={family.id}
              kind={family.kind}
              focusId={personId}
              me={me}
              onStart={(id) => navigate(`/circle/${family.id}/people/${id}`)}
            />
          </div>
        )}
        {view === 'connections' &&
          (shown.length > 0 ? (
            <div className="flex flex-col gap-4">
              <PeopleGroups groups={shown} circles={several} onOpen={openPerson} />
              {showing === 'circle' && !someone && family.members.length === 1 && (
                <p className="whisper">Every circle starts with one archivist. That’s you.</p>
              )}
            </div>
          ) : (
            (showing === 'circle' || everyone.data) && (
              <p className="text-muted-foreground">No one here matches. Try another name, or Everyone under Filter.</p>
            )
          ))}
      </div>

      <PersonProfile person={profile} onClose={() => setProfile(null)} />
      {withTree && tree.data && someone && (
        <AddPerson
          key={`${from}-${adding}`}
          open={adding}
          onOpenChange={setAdding}
          familyId={family.id}
          tree={tree.data}
          from={from}
        />
      )}
      <Dialog open={inviting} onOpenChange={setInviting}>
        <DialogContent className="max-h-[90svh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Invite someone</DialogTitle>
            <DialogDescription>
              One invite for one person, to join {family.name}. It works once, for 7 days.
            </DialogDescription>
          </DialogHeader>
          <InviteForm family={family} />
        </DialogContent>
      </Dialog>
      <SpaceDialog
        family={family}
        me={me.id}
        editing={makingSpace ? 'new' : null}
        onClose={() => setMakingSpace(false)}
      />
      {add.dialogs}
    </div>
  )
}

// The circle's own settings: the names, its spaces, a copy of everything, and leaving.
function CircleSettings({ family, me }: { family: Family; me: Me }) {
  usePageFrame(family.id, {
    title: 'Circle settings',
    back: { to: `/circle/${family.id}`, label: 'Back to home' },
  })
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const leave = useMutation({
    mutationFn: () => api(`/families/${family.id}/leave`, { method: 'POST' }),
    onSuccess: async () => {
      forgetFamily()
      await queryClient.invalidateQueries({ queryKey: ['families'] })
      await queryClient.invalidateQueries({ queryKey: ['circles'] })
      navigate('/')
    },
  })
  return (
    <>
      <PlaceHeader family={family} title="Circle settings" />
      <section className="flex flex-col gap-4" aria-labelledby="names">
        <h2 id="names" className="sr-only">
          Names
        </h2>
        <HomeName family={family} />
        <CircleName family={family} />
        <CircleColorPicker family={family} />
      </section>
      {!family.personal && <CircleSpaces family={family} me={me.id} />}
      <GrowCircle family={family} me={me} />
      <section className="flex flex-col gap-4 border-t pt-8" aria-labelledby="keep">
        <h2 id="keep" className="text-title">
          Keep a copy
        </h2>
        <Link
          to={`/circle/${family.id}/copy`}
          className="flex items-center gap-2 self-start rounded-md text-sm text-brand underline underline-offset-4 outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
        >
          <Download className="size-4" aria-hidden /> Keep a copy of everything
        </Link>
      </section>
      {/* Your own space stays yours. */}
      {!family.personal && (
        <section className="flex flex-col gap-4 border-t pt-8" aria-labelledby="leave">
          <h2 id="leave" className="text-title">
            Leave
          </h2>
          <FormError error={leave.error} />
          <ConfirmButton
            label="Leave this circle"
            title={`Leave ${family.name}?`}
            description="What you’ve added stays with the circle. Someone can invite you back any time."
            confirm="Leave"
            cancel="Stay"
            disabled={leave.isPending}
            onConfirm={() => leave.mutate()}
          />
        </section>
      )}
    </>
  )
}

function KeepACopy({ family }: { family: Family }) {
  usePageFrame(family.id, {
    title: 'Keep a copy',
    back: { to: `/circle/${family.id}/settings`, label: 'Back to circle settings' },
  })
  return (
    <>
      <PlaceHeader family={family} title="Keep a copy" />
      <p className="text-muted-foreground">
        Download everything in this circle: every original card photo and every recipe, in a file that opens without
        gatheredfrom.
      </p>
      <Button asChild variant="outline" className="self-start">
        <a href={`/api/families/${family.id}/export`} download>
          <Download aria-hidden /> Download everything
        </a>
      </Button>
    </>
  )
}

// Your home's name ("The Johnson family"): what you see when you open the app. Anyone who lives there can rename it.
function HomeName({ family }: { family: Family }) {
  const queryClient = useQueryClient()
  const [name, setName] = useState(family.home?.name ?? '')
  const rename = useMutation({
    mutationFn: (name: string) => api(`/families/${family.id}/home`, { method: 'PATCH', body: { name } }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['family', family.id] })
      await queryClient.invalidateQueries({ queryKey: ['families'] })
      await queryClient.invalidateQueries({ queryKey: ['circles'] })
    },
  })
  if (!family.home) return null
  return (
    <form
      className="grid gap-2"
      onSubmit={(e) => {
        e.preventDefault()
        rename.mutate(name.trim())
      }}
    >
      <Label htmlFor="home-name">Your home</Label>
      <div className="flex gap-2">
        <Input id="home-name" required maxLength={80} value={name} onChange={(e) => setName(e.target.value)} />
        <Button type="submit" variant="outline" disabled={rename.isPending || name.trim() === family.home.name}>
          Rename
        </Button>
      </div>
      <FormError error={rename.error} />
    </form>
  )
}
